import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getEventById, registerForEvent } from '../../utils/events';
import { sanitizeEmail, sanitizePhone, sanitizeText } from '../../utils/inputSanitizer';
import { useParams } from 'react-router-dom';
import SEO from '../seo/SEO';

function EventDetails() {
	const { eventId } = useParams();
	const [eventItem, setEventItem] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [success, setSuccess] = useState('');
	const [error, setError] = useState('');
	const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });

	useEffect(() => {
		let mounted = true;
		(async () => {
			const ev = await getEventById(eventId);
			if (mounted) {
				setEventItem(ev);
				setLoading(false);
			}
		})();
		return () => { mounted = false; };
	}, [eventId]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		const cleanedValue =
			name === 'email'
				? sanitizeEmail(value)
				: name === 'phone'
				? sanitizePhone(value)
				: sanitizeText(value);
		setForm(prev => ({ ...prev, [name]: cleanedValue }));
	};

	const handleRegister = async (e) => {
		e.preventDefault();
		setError('');
		setSuccess('');
		if (!form.name || !form.email) {
			setError('Please enter your name and email.');
			return;
		}
		setSaving(true);
		try {
			await registerForEvent(eventId, form, eventItem?.title || 'event');
			setSuccess('Registration received! Check your email for updates.');
			setForm({ name: '', email: '', phone: '', notes: '' });
		} catch (e) {
			setError('Could not submit registration. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	if (loading) return <p className="text-gray-600">Loading...</p>;
	if (!eventItem) return <p className="text-gray-600">Event not found.</p>;

	const start = eventItem.startAt?.toDate ? eventItem.startAt.toDate() : new Date(eventItem.startAt);
	const end = eventItem.endAt?.toDate ? eventItem.endAt.toDate() : new Date(eventItem.endAt);
	const past = end < new Date();

	// Prepare event schema
	const eventSchema = {
		"@context": "https://schema.org",
		"@type": "Event",
		"name": eventItem.title,
		"description": eventItem.description,
		"startDate": start.toISOString(),
		"endDate": end.toISOString(),
		"eventAttendanceMode": eventItem.isOnline ? "https://schema.org/OnlineEventAttendanceMode" : "https://schema.org/OfflineEventAttendanceMode",
		"eventStatus": past ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled",
		"location": eventItem.isOnline ? {
			"@type": "VirtualLocation",
			"url": window.location.href
		} : {
			"@type": "Place",
			"name": eventItem.location || "Physical Location"
		},
		"image": [eventItem.bannerImageUrl],
		"organizer": {
			"@type": "Organization",
			"name": "Intellectual Oasis Fellowship",
			"url": "https://iofellowship.org"
		}
	};

	const shareData = {
		title: eventItem.title,
		text: `${eventItem.title} — ${eventItem.isOnline ? 'Online' : eventItem.location} • ${start.toLocaleString()}`,
		url: window.location.href
	};

	const copyLink = async () => {
		try {
			await navigator.clipboard.writeText(window.location.href);
			setSuccess('Link copied to clipboard');
		} catch (e) {
			setError('Could not copy link');
		}
	};

	return (
		<>
			<SEO
				title={eventItem.title}
				description={eventItem.description?.substring(0, 160) || `Join us for ${eventItem.title}`}
				pathname={`/events/${eventId}`}
				image={eventItem.bannerImageUrl}
			/>
			<Helmet>
				<script type="application/ld+json">
					{JSON.stringify(eventSchema)}
				</script>
			</Helmet>
			<div className="max-w-4xl mx-auto">
				<div className="mb-6">
				<h1 className="text-3xl font-extrabold text-gray-900">{eventItem.title}</h1>
				<p className="text-gray-700">{eventItem.isOnline ? 'Online' : eventItem.location}</p>
				<p className="text-sm text-gray-600">{start.toLocaleString()} — {end.toLocaleString()}</p>
				<div className="mt-3 flex flex-wrap gap-2">
					{navigator.share && (
						<button onClick={() => navigator.share(shareData)} className="px-3 py-1 border rounded-md text-sm">Share</button>
					)}
					<button onClick={copyLink} className="px-3 py-1 border rounded-md text-sm">Copy link</button>
					<a href={`https://wa.me/?text=${encodeURIComponent(shareData.text + ' ' + shareData.url)}`} target="_blank" rel="noreferrer" className="px-3 py-1 border rounded-md text-sm">WhatsApp</a>
					<a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData.text)}&url=${encodeURIComponent(shareData.url)}`} target="_blank" rel="noreferrer" className="px-3 py-1 border rounded-md text-sm">X</a>
					<a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}`} target="_blank" rel="noreferrer" className="px-3 py-1 border rounded-md text-sm">Facebook</a>
				</div>
			</div>
			{eventItem.bannerImageUrl && (
				<div className="mb-6">
					<img src={eventItem.bannerImageUrl} alt={eventItem.title} className="w-full rounded-lg" />
				</div>
			)}
			<p className="prose max-w-none text-gray-800 mb-8 whitespace-pre-line">{eventItem.description}</p>

			<div className="bg-white border rounded-lg p-6 shadow-sm">
				<h2 className="text-xl font-bold mb-4">Register</h2>
				{past ? (
					<p className="text-gray-600">This event has ended.</p>
				) : eventItem.registrationUrl ? (
					<a href={eventItem.registrationUrl} target="_blank" rel="noreferrer" className="inline-flex items-center px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:opacity-90">Register on external site</a>
				) : (
					<form onSubmit={handleRegister} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-gray-700">Name</label>
							<input name="name" value={form.name} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" required />
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700">Email</label>
							<input type="email" name="email" value={form.email} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" required />
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700">Phone</label>
							<input
						type="tel"
						name="phone"
						value={form.phone}
						onChange={handleChange}
						inputMode="tel"
						pattern="[0-9+\-() ]*"
						className="mt-1 w-full border rounded-md px-3 py-2"
					/>
						</div>
						<div className="sm:col-span-2">
							<label className="block text-sm font-medium text-gray-700">Notes</label>
							<textarea name="notes" rows="3" value={form.notes} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" />
						</div>
						{error && <p className="sm:col-span-2 text-sm text-red-600">{error}</p>}
						{success && <p className="sm:col-span-2 text-sm text-green-600">{success}</p>}
						<div className="sm:col-span-2">
							<button type="submit" disabled={saving} className="inline-flex items-center px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:opacity-90 disabled:opacity-60">
								{saving ? 'Submitting...' : 'Register'}
							</button>
						</div>
					</form>
				)}
			</div>
		</div>
		</>
	);
}

export default EventDetails;
